import React from 'react';
import { Task, Country } from '../types/todo';
import { TaskList } from '../components/TaskList';
import { TaskForm } from '../components/TaskForm';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';
import { GuideModal } from '../components/GuideModal';
import { TodoListHeader } from '../components/TodoListHeader';
import { PreviewOverlay } from '../components/PreviewOverlay';
import { TaskNavigation } from '../components/TaskNavigation';
import { TravelDates } from '../components/TravelDates';
import { TutorialPromo } from '../components/TutorialPromo';
import { GuidePromo } from '../components/GuidePromo';
import { BudgetSimulatorPromo } from '../components/BudgetSimulatorPromo';
import { QuickLinksSection } from '../components/QuickLinksSection';
import { useTaskManagement } from '../hooks/useTaskManagement';
import { getCountriesFromFirebase, getUserLists } from '../lib/firebase-service';
import { MessageSquare, ArrowRight } from 'lucide-react';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { sendEmail } from '../lib/api';
import { formatChecklistForEmail } from '../utils/checklist';
import { useAuth } from '../lib/auth-context';

interface TodoListPageProps {
  countryCode: string;
  onBack: () => void;
  isPreview?: boolean;
  onAddList?: () => void;
  onDeleteList?: () => void;
  userId?: string;
  isCreatingList?: boolean;
}

export function TodoListPage({
  countryCode,
  onBack,
  isPreview = false,
  onAddList,
  onDeleteList,
  userId,
  isCreatingList = false
}: TodoListPageProps) {
  const [country, setCountry] = React.useState<Country | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const { userInfo } = useAuth();
  const [listCreatedAt, setListCreatedAt] = React.useState<Date | undefined>();
  const [showHeaderGuide, setShowHeaderGuide] = React.useState(false);
  const [messageStats, setMessageStats] = React.useState<{ totalMessages: number }>({
    totalMessages: 0
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const countries = await getCountriesFromFirebase();
        const foundCountry = countries.find(c => c.code === countryCode);
        if (foundCountry) {
          setCountry(foundCountry);
        }

        if (!isPreview && userId) {
          const lists = await getUserLists(userId);
          const userList = lists.find(list => list.countryCode === countryCode);
          if (userList) {
            setListCreatedAt(userList.createdAt);
          }
        }

        const messagesRef = collection(db, 'messages');
        const q = query(
          messagesRef,
          where('thread_code', '==', countryCode),
          where('status', '==', 'active')
        );
        const totalMessagesSnapshot = await getCountFromServer(q);
        setMessageStats({ totalMessages: totalMessagesSnapshot.data().count });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [countryCode, isPreview, userId]);

  const getOptimizedImageUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('unsplash.com')) {
        const baseUrl = url.split('?')[0];
        return `${baseUrl}?w=3000&q=85&fit=crop&crop=entropy&auto=format`;
      }
      return url;
    } catch (e) {
      console.error('Invalid image URL:', e);
      return url;
    }
  };

  const {
    tasks,
    departureDate,
    returnDate,
    isAddingTask,
    editingTask,
    showResetConfirmation,
    showGuide,
    taskToDelete,
    handleUpdateStatus,
    handleDeleteTask,
    handleConfirmDeleteTask,
    handleAddSubtask,
    handleToggleSubtask,
    handleCreateTask,
    handleUpdateTask,
    handleUpdateNotes,
    handleResetToDefaults,
    handleTaskClick,
    handleDeleteSubtask,
    handleUpdateDepartureDate,
    handleUpdateReturnDate,
    handleUpdatePriority,
    handleCloseGuide,
    setIsAddingTask,
    setEditingTask,
    setShowResetConfirmation,
    setShowGuide,
    setTaskToDelete
  } = useTaskManagement(countryCode, isPreview, userId);

  const progressStats = React.useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'completed').length;
    const inProgress = tasks.filter(t => t.status === 'in-progress').length;
    return {
      total,
      completed,
      inProgress,
      notStarted: total - completed - inProgress,
      completionPercentage: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [tasks]);

  const daysUntilDeparture = React.useMemo(() => {
    if (!departureDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const departure = new Date(departureDate);
    const diffTime = departure.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [departureDate]);

  if (isLoading || !country) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-3 border-gray-300 border-t-gray-900 dark:border-gray-600 dark:border-t-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-[8.5rem] pb-mobile main-content">
      <TodoListHeader
        country={country}
        onBack={onBack}
        isPreview={isPreview}
        onAddList={onAddList}
        onDeleteList={onDeleteList}
        onShowGuide={() => setShowHeaderGuide(true)}
        onShowResetConfirmation={() => setShowResetConfirmation(true)}
        onAddTask={() => setIsAddingTask(true)}
        showCompleted={false}
        onToggleShowCompleted={() => {}}
        onSendEmail={async () => {
          const button = document.querySelector('button[title="Envoyer par email"]');
          if (button) {
            button.classList.add('pointer-events-none', 'opacity-50');
          }
          if (!userInfo?.user?.email) return;
          try {
            await sendEmail(
              `Checklist PVT ${country.name}`,
              formatChecklistForEmail({ tasks, country, departureDate, returnDate }),
              userInfo.user.email,
              userInfo
            );
            const alert = document.createElement('div');
            alert.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-6 py-3 rounded-xl shadow-lg border border-emerald-100 dark:border-emerald-800/30 z-50 flex items-center gap-3 animate-fade-in';
            alert.innerHTML = `
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Checklist envoyee par email avec succes !</span>
            `;
            document.body.appendChild(alert);
            setTimeout(() => alert.remove(), 5000);
          } catch (error) {
            console.error('Error sending checklist email:', error);
            const alert = document.createElement('div');
            alert.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-6 py-3 rounded-xl shadow-lg border border-red-100 dark:border-red-800/30 z-50 flex items-center gap-3 animate-fade-in';
            alert.innerHTML = `
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>Erreur lors de l'envoi de l'email</span>
            `;
            document.body.appendChild(alert);
            setTimeout(() => alert.remove(), 5000);
          } finally {
            if (button) {
              button.classList.remove('pointer-events-none', 'opacity-50');
            }
          }
        }}
      />

      {/* Hero Section */}
      <div className="relative w-full h-36 sm:h-72 md:h-80 overflow-hidden">
        <img
          src={getOptimizedImageUrl(country.image)}
          alt={country.name}
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />

        {/* Overlay Content */}
        <div className="absolute inset-x-0 bottom-0 p-3 sm:p-6">
          <div className="flex items-end justify-between gap-3">
            <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-white/15 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center border border-white/20 flex-shrink-0">
                <span className="text-lg sm:text-xl">{country.flag}</span>
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-white truncate">PVT {country.name}</h2>
                {!isPreview && (
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex items-center gap-1.5">
                      <div className="w-16 sm:w-24 h-1 bg-white/20 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-400 rounded-full transition-all duration-500"
                          style={{ width: `${progressStats.completionPercentage}%` }}
                        />
                      </div>
                      <span className="text-white/80 text-[11px] sm:text-xs font-medium">
                        {progressStats.completed}/{progressStats.total}
                      </span>
                    </div>
                    {departureDate && daysUntilDeparture !== null && daysUntilDeparture > 0 && (
                      <>
                        <span className="w-0.5 h-0.5 rounded-full bg-white/40" />
                        <span className="text-white/60 text-[11px] sm:text-xs">J-{daysUntilDeparture}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!isPreview && messageStats.totalMessages > 0 && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openMessageCenter'))}
                className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white text-xs rounded-lg transition-colors border border-white/20 flex-shrink-0"
              >
                <MessageSquare className="w-3.5 h-3.5" />
                <span>{messageStats.totalMessages}</span>
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-[100rem] mx-auto mt-2 sm:mt-8">
        <div className="lg:flex lg:gap-6 px-0 sm:px-4">
          {/* Left Sidebar - Navigation */}
          {!isPreview && (
            <div className="hidden lg:block lg:w-72 flex-shrink-0">
              <div className="sticky top-[calc(8.5rem+1rem)] h-[calc(100vh-8.5rem-2rem)] overflow-y-auto">
                <TaskNavigation
                  tasks={tasks}
                  onTaskClick={handleTaskClick}
                  onUpdateStatus={handleUpdateStatus}
                />
              </div>
            </div>
          )}

          {/* Center Content */}
          <div className="flex-1 min-w-0">
            {!isPreview && (
              <div className="space-y-2.5 sm:space-y-4 mb-4 sm:mb-6 px-2 sm:px-0">
                <TutorialPromo country={country} />
                <TravelDates
                  departureDate={departureDate}
                  returnDate={returnDate}
                  onDepartureDateChange={handleUpdateDepartureDate}
                  onReturnDateChange={handleUpdateReturnDate}
                />
              </div>
            )}

            <div className={isPreview ? 'opacity-75' : ''}>
              <TaskList
                tasks={tasks}
                departureDate={departureDate}
                returnDate={returnDate}
                onUpdateStatus={handleUpdateStatus}
                onDeleteTask={handleDeleteTask}
                onAddSubtask={handleAddSubtask}
                onToggleSubtask={handleToggleSubtask}
                onUpdateNotes={handleUpdateNotes}
                onDeleteSubtask={handleDeleteSubtask}
                isPreview={isPreview}
                onEditTask={setEditingTask}
                onUpdatePriority={handleUpdatePriority}
              />
            </div>

            {isPreview && onAddList && (
              <PreviewOverlay
                onAddList={onAddList}
                onBack={onBack}
                isCreating={isCreatingList}
              />
            )}

            {!isPreview && (
              <>
                <div className="mt-8">
                  <QuickLinksSection />
                </div>
                <div className="xl:hidden mt-6 space-y-4">
                  <BudgetSimulatorPromo />
                  <GuidePromo />
                </div>
              </>
            )}
          </div>

          {/* Right Sidebar - Promos */}
          {!isPreview && (
            <div className="hidden xl:block w-72 flex-shrink-0">
              <div className="sticky top-[calc(8.5rem+1rem)] space-y-4">
                <BudgetSimulatorPromo />
                <GuidePromo />
              </div>
            </div>
          )}
        </div>
      </div>

      {isAddingTask && !isPreview && (
        <TaskForm
          onSubmit={handleCreateTask}
          onClose={() => setIsAddingTask(false)}
        />
      )}

      {editingTask && !isPreview && (
        <TaskForm
          initialTask={editingTask}
          onSubmit={handleUpdateTask}
          onClose={() => setEditingTask(null)}
        />
      )}

      <DeleteConfirmationModal
        isOpen={showResetConfirmation}
        onClose={() => setShowResetConfirmation(false)}
        onConfirm={handleResetToDefaults}
        itemName="votre checklist"
        confirmationWord="RÉINITIALISER"
        description="Cette action va réinitialiser toutes vos tâches à leur état initial. Vos modifications seront perdues. Cette action est irréversible."
      />

      <DeleteConfirmationModal
        isOpen={!!taskToDelete}
        onClose={() => setTaskToDelete(null)}
        onConfirm={handleConfirmDeleteTask}
        itemName={`la tâche "${taskToDelete?.title}"`}
        confirmationWord="SUPPRIMER"
      />

      <GuideModal
        isOpen={showGuide}
        onClose={handleCloseGuide}
        isIntroGuide={true}
      />

      <GuideModal
        isOpen={showHeaderGuide}
        onClose={() => setShowHeaderGuide(false)}
        isIntroGuide={false}
      />
    </div>
  );
}
